/**
 * Backend stall detector. When Postgres saturates, Hasura answers with statement timeouts ("database query error",
 * 57014, also what the DB watchdog's cancels look like) and the gateway with 502/503/504. Clients that keep retrying
 * and polling at full speed then hold the database down (context/db-performance). This module notices that pattern
 * and lets every retry/poll loop slow down until the backend has been quiet for a while.
 *
 * - stalled: STALL_THRESHOLD stall signals within STALL_WINDOW_MS.
 * - recovered: no stall signal for STALL_QUIET_MS.
 * - while stalled: retries back off exponentially with full jitter (cap 60 s), polls run STALL_POLL_FACTOR times
 *   less often, and non-critical requests (reports, exports, dashboards) are not retried at all.
 *
 * Plain TypeScript, no framework: the same file lives in packages/mirutasegura-app/src/services/backendStall.ts.
 * Keep the two in sync.
 */

/**
 * Only the backend's own answers count. A client timeout is not one: on a weak signal (a driver in a dead zone) every
 * request hangs until it times out, which looked like a stall and showed the notice while the server was idle.
 */
export type StallSignal = 'db-timeout' | 'gateway'

/** Three failures within 20 s: one slow query is not a stall, a burst is. */
export const STALL_THRESHOLD = 3
export const STALL_WINDOW_MS = 20_000
/** Stalled until no stall signal for 30 s (one watchdog pass is 20 s apart). */
export const STALL_QUIET_MS = 30_000
export const BACKOFF_BASE_MS = 500
export const STALLED_BACKOFF_BASE_MS = 2_000
export const BACKOFF_CAP_MS = 60_000
export const STALL_POLL_FACTOR = 4

/** The one notice users see (tú, calm, non-blocking). */
export const STALL_NOTICE = 'Hay alta demanda; reintentamos en unos segundos.'

type Timer = ReturnType<typeof setTimeout>

export interface StallDetectorOptions {
  threshold?: number
  windowMs?: number
  quietMs?: number
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => Timer
  clearTimer?: (timer: Timer) => void
}

export interface StallDetector {
  report: (signal: StallSignal) => void
  isStalled: () => boolean
  subscribe: (listener: (stalled: boolean) => void) => () => void
  reset: () => void
}

export function createStallDetector(options: StallDetectorOptions = {}): StallDetector {
  const threshold = options.threshold ?? STALL_THRESHOLD
  const windowMs = options.windowMs ?? STALL_WINDOW_MS
  const quietMs = options.quietMs ?? STALL_QUIET_MS
  const now = options.now ?? Date.now
  const setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
  const clearTimer = options.clearTimer ?? ((timer) => clearTimeout(timer))
  const listeners = new Set<(stalled: boolean) => void>()
  let signals: number[] = []
  let stalled = false
  let lastSignal = 0
  let quietTimer: Timer | null = null

  const emit = () => listeners.forEach((listener) => {
    try { listener(stalled) } catch { /* a listener never breaks detection */ }
  })

  const scheduleRecovery = () => {
    if (quietTimer) clearTimer(quietTimer)
    quietTimer = setTimer(() => {
      quietTimer = null
      const quietFor = now() - lastSignal
      if (quietFor >= quietMs) {
        stalled = false
        signals = []
        emit()
      } else {
        scheduleRecovery()
      }
    }, Math.max(0, quietMs - (now() - lastSignal)))
  }

  return {
    report() {
      const t = now()
      lastSignal = t
      signals = signals.filter((at) => t - at < windowMs)
      signals.push(t)
      if (!stalled && signals.length >= threshold) {
        stalled = true
        emit()
      }
      if (stalled) scheduleRecovery()
    },
    isStalled: () => stalled,
    subscribe(listener) {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
    reset() {
      if (quietTimer) clearTimer(quietTimer)
      quietTimer = null
      signals = []
      const was = stalled
      stalled = false
      if (was) emit()
    },
  }
}

type GraphQLErrorLike = { message?: string; extensions?: Record<string, any> }

const DB_TIMEOUT_MESSAGE = /canceling statement due to (statement timeout|user request)|database query error/i

/** Hasura's answer when Postgres cancelled the statement (timeout or the watchdog). */
export function classifyGraphQLErrors(errors: readonly GraphQLErrorLike[] | null | undefined): StallSignal | null {
  for (const error of errors ?? []) {
    const internal = error?.extensions?.internal?.error
    if (DB_TIMEOUT_MESSAGE.test(error?.message ?? '') || DB_TIMEOUT_MESSAGE.test(internal?.message ?? '')) return 'db-timeout'
    if (internal?.status_code === '57014') return 'db-timeout'
  }
  return null
}

export function classifyHttpStatus(status: number | null | undefined): StallSignal | null {
  return status === 502 || status === 503 || status === 504 ? 'gateway' : null
}

/**
 * Delay before retry number `attempt` (1-based): exponential with full jitter, capped at 60 s. While stalled the base
 * is higher, so even the first retry waits a few seconds instead of joining the stampede.
 */
export function backoffDelay(attempt: number, stalled: boolean, random: () => number = Math.random): number {
  const base = stalled ? STALLED_BACKOFF_BASE_MS : BACKOFF_BASE_MS
  const ceiling = Math.min(BACKOFF_CAP_MS, base * 2 ** Math.max(0, attempt - 1))
  return Math.round(random() * ceiling)
}

/** Reports, exports and dashboards: useful, never urgent. While stalled they get no retries at all. */
const NON_CRITICAL = /(^|\/)(report|export|dashboard|history)/i

export function isNonCriticalRequest(name: string | null | undefined): boolean {
  return !!name && NON_CRITICAL.test(name)
}

/**
 * A poll tick gate: `allow()` is true when a tick should run. Hidden pages skip their ticks (the caller catches up
 * when the page is visible again); while stalled only every STALL_POLL_FACTOR-th interval goes through.
 */
export function createPollGate(
  intervalMs: number,
  detector: Pick<StallDetector, 'isStalled'>,
  options: { now?: () => number; isVisible?: () => boolean } = {},
) {
  const now = options.now ?? Date.now
  const isVisible = options.isVisible ?? (() => typeof document === 'undefined' || document.visibilityState !== 'hidden')
  let lastRun = 0
  return {
    allow(): boolean {
      if (!isVisible()) return false
      const t = now()
      const every = detector.isStalled() ? intervalMs * STALL_POLL_FACTOR : intervalMs
      // 10% slack so a timer that fires a little early is not skipped a whole interval.
      if (lastRun && t - lastRun < every * 0.9) return false
      lastRun = t
      return true
    },
  }
}

/** The app-wide detector. */
export const backendStall = createStallDetector()
